//////////////////
// IMPORTS
//////////////////
// schema
import * as ISchema from "#webapp/src/__schema__";

import { warnOutsideTests } from "#webapp/src/util/helpers.util";
import { ZonedMoment } from "@clockwise/client-commons/src/util/ZonedMoment";
import { getAbsoluteDateTime } from "@clockwise/client-commons/src/util/format-time";
import { getRenderTimeZone } from "@clockwise/web-commons/src/util/time-zone.util";
import moment from "moment-timezone";

export { getAttendeesText } from "@clockwise/client-commons/src/util/scheduling-slot";

//////////////////
// TYPES
//////////////////
export enum ESlotTagType {
  Best = "Best",
  Soonest = "Soonest",
  Good = "Good",
  Current = "Current",
}

export interface ISlotInfo {
  dateText: string;
  hasRooms: boolean;
  isBest: boolean;
  isGood: boolean;
  isSoon: boolean;
  isCurrent: boolean;
  rooms: string[];
  score: number;
  scoreText: string;
  scoreSuffix: string;
  tagText: string;
}

function checkForBadFragment(slot: ISchema.ISchedulingSlot) {
  const isIncomplete =
    !slot?.timeRange?.startTime?.dateOrDateTime || !slot?.timeRange?.endTime?.dateOrDateTime;
  if (isIncomplete) {
    warnOutsideTests("processSlot passed incomplete slot; check your fragments!");
  }
  return isIncomplete;
}

//////////////////
// TAGs
//////////////////
function deltaHasTag(
  tags: ISchema.SchedulingAttendanceSummaryTag[],
  tag: ISchema.SchedulingAttendanceSummaryTag,
) {
  return tags.indexOf(tag) > -1;
}

export function isBestTime(
  slot: ISchema.ISchedulingSlot,
  qualifiers?: ISchema.SchedulingResponseQualifier[],
) {
  const noConvenientTimes =
    qualifiers &&
    qualifiers.indexOf("NoConvenientTimesWithRooms" as ISchema.SchedulingResponseQualifier) !== -1;
  return deltaHasTag(slot.summary.tags, ESlotTagType.Best as any) && !noConvenientTimes; // FIXME: TEMP FIX FOR GQL2TS ISSUES
}

export function isSoonTime(slot: ISchema.ISchedulingSlot) {
  return deltaHasTag(slot.summary.tags, ESlotTagType.Soonest as any); // FIXME: TEMP FIX FOR GQL2TS ISSUES
}

export function isGoodTime(slot: ISchema.ISchedulingSlot) {
  return deltaHasTag(slot.summary.tags, ESlotTagType.Good as any); // FIXME: TEMP FIX FOR GQL2TS ISSUES
}

export function isCurrentTime(slot: ISchema.ISchedulingSlot) {
  return deltaHasTag(slot.summary.tags, ESlotTagType.Current as any); // FIXME: TEMP FIX FOR GQL2TS ISSUES
}

//////////////////
// SLOT FNs
//////////////////
export type GetMomentForSlotSlot = {
  timeRange: {
    startTime: {
      dateOrDateTime: string;
    };
  };
};
export function getMomentForSlot(slot: GetMomentForSlotSlot) {
  return moment.tz(
    slot.timeRange.startTime.dateOrDateTime,
    "YYYY-MM-DD HH:mma",
    getRenderTimeZone(),
  );
}

// pull the suggested time off the suggested slot and format it nicely
export function getSuggestedDateText(slot: ISchema.ISchedulingSlot) {
  const suggestionZonedMoment = new ZonedMoment(getMomentForSlot(slot));
  return getAbsoluteDateTime(suggestionZonedMoment);
}

// get the FocusTime diff in a nicely formatted string
export function getScoreText(score: number) {
  if (score === 0) {
    return "No impact";
  }
  let scoreText = score > 0 ? "-" : "+";
  const absoluteValue = score < 0 ? score * -1 : score;
  scoreText += absoluteValue % 1 === 0 ? absoluteValue : absoluteValue.toFixed(1);
  scoreText += "h";
  return scoreText;
}

// get the FocusTime diff in a nicely formatted string
export function getScoreSuffix(score: number) {
  const suffix = score === 0 ? " on Focus Time" : " Focus Time";

  return suffix;
}

// centralizing all of the front-end info needed to render a scored suggested slot
export function processSlot(
  slot?: ISchema.ISchedulingSlot,
  qualifiers?: ISchema.SchedulingResponseQualifier[],
): ISlotInfo | null {
  if (!slot || checkForBadFragment(slot)) {
    return null;
  }

  const isBest = isBestTime(slot, qualifiers);
  const isGood = isGoodTime(slot);
  const isSoon = isSoonTime(slot);
  const isCurrent = isCurrentTime(slot);

  const rooms = slot.summary.availableRooms;
  const hasRooms = rooms.length > 0;
  const dateText = getSuggestedDateText(slot);
  const score = slot.summary.focusTimeMinutesCost / 60;
  const scoreText = getScoreText(score);
  const scoreSuffix = getScoreSuffix(score);

  let tagText = "";
  if (isBest) {
    tagText = "BEST TIME";
  } else if (isSoon) {
    tagText = "FIRST AVAILABLE";
  } else if (isGood) {
    tagText = "GOOD TIME";
  } else if (isCurrent) {
    tagText = "CURRENT TIME";
  }

  return {
    dateText,
    hasRooms,
    isBest,
    isSoon,
    isGood,
    isCurrent,
    rooms,
    score,
    scoreText,
    scoreSuffix,
    tagText,
  };
}
